/**
 * Swagger 获取模块
 * 支持多种方式获取 Swagger JSON
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import * as fs from 'fs';
import * as path from 'path';

/**
 * Swagger 获取配置 - 通过 HTTP 请求获取
 */
export interface SwaggerHttpConfig {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  params?: Record<string, any>;
  data?: any;
  timeout?: number;
  axiosConfig?: AxiosRequestConfig;
}

/**
 * Swagger 获取函数类型
 * @param axios - Axios 实例，用户可以使用这个实例发起请求
 * @returns Promise<string> - 返回 Swagger JSON 字符串
 */
export type SwaggerFetcherFunction = (axios: AxiosInstance) => Promise<string>;

/**
 * Swagger 数据源类型
 * - string: 直接传入 Swagger JSON 字符串
 * - SwaggerHttpConfig: HTTP 请求配置
 * - SwaggerFetcherFunction: 自定义获取函数
 */
export type SwaggerSource = string | SwaggerHttpConfig | SwaggerFetcherFunction;

/**
 * 判断是否为 HTTP 配置对象
 */
const isSwaggerHttpConfig = (source: any): source is SwaggerHttpConfig => {
  return source && typeof source === 'object' && typeof source.url === 'string';
};

/**
 * 判断是否为自定义获取函数
 */
const isSwaggerFetcherFunction = (source: any): source is SwaggerFetcherFunction => {
  return typeof source === 'function';
};

/**
 * 通过 HTTP 请求获取 Swagger
 */
const fetchSwaggerByHttp = async (config: SwaggerHttpConfig): Promise<string> => {
  const {
    url,
    method = 'GET',
    headers = {},
    params,
    data,
    timeout = 30000,
    axiosConfig = {},
  } = config;

  try {
    const response = await axios.request({
      url,
      method,
      headers,
      params,
      data,
      timeout,
      ...axiosConfig,
    });

    let swaggerData = response.data;

    // 处理嵌套响应（如 {result: 'ok', data: {...swagger...}}）
    if (
      swaggerData &&
      typeof swaggerData === 'object' &&
      'data' in swaggerData &&
      swaggerData.data &&
      typeof swaggerData.data === 'object'
    ) {
      // 检查是否是 Kepler/Yapi 风格的响应
      if ('result' in swaggerData || 'code' in swaggerData || 'errcode' in swaggerData) {
        swaggerData = swaggerData.data;
      }
    }

    // 如果响应数据是对象，转换为字符串
    if (typeof swaggerData === 'object') {
      return JSON.stringify(swaggerData);
    }

    // 如果已经是字符串，直接返回
    if (typeof swaggerData === 'string') {
      return swaggerData;
    }

    throw new Error('Invalid Swagger response format');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to fetch Swagger from ${url}: ${error.message}${
          error.response ? ` (Status: ${error.response.status})` : ''
        }`
      );
    }
    throw error;
  }
};

/**
 * 通过自定义函数获取 Swagger
 */
const fetchSwaggerByFunction = async (fetcher: SwaggerFetcherFunction): Promise<string> => {
  try {
    const result = await fetcher(axios);

    // 如果返回的是对象，转换为字符串
    if (typeof result === 'object') {
      return JSON.stringify(result);
    }

    // 如果已经是字符串，直接返回
    if (typeof result === 'string') {
      return result;
    }

    throw new Error('Fetcher function must return a string or object');
  } catch (error) {
    throw new Error(
      `Failed to fetch Swagger using custom function: ${(error as Error).message}`
    );
  }
};

/**
 * 获取 Swagger JSON
 * @param source - Swagger 数据源（字符串、HTTP 配置或自定义函数）
 * @returns Promise<string> - Swagger JSON 字符串
 */
export const fetchSwagger = async (source: SwaggerSource): Promise<string> => {
  // 1. 如果是字符串，直接返回
  if (typeof source === 'string') {
    return source;
  }

  // 2. 如果是 HTTP 配置，通过 HTTP 请求获取
  if (isSwaggerHttpConfig(source)) {
    console.log(`正在从 ${source.url} 获取 Swagger...`);
    const swagger = await fetchSwaggerByHttp(source);
    console.log('✅ Swagger 获取成功');
    return swagger;
  }

  // 3. 如果是自定义函数，调用函数获取
  if (isSwaggerFetcherFunction(source)) {
    console.log('正在通过自定义函数获取 Swagger...');
    const swagger = await fetchSwaggerByFunction(source);
    console.log('✅ Swagger 获取成功');
    return swagger;
  }

  throw new Error('Invalid Swagger source type');
};

/**
 * 验证 Swagger JSON 是否有效
 */
export const validateSwagger = (swaggerJson: string): boolean => {
  try {
    const swagger = JSON.parse(swaggerJson);
    
    // 基本验证：检查是否有 paths 字段
    if (!swagger || typeof swagger !== 'object') {
      return false;
    }

    // Swagger 2.0 格式验证
    if (swagger.swagger === '2.0' && swagger.paths) {
      return true;
    }

    // OpenAPI 3.0+ 格式验证
    if (swagger.openapi && swagger.paths) {
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
};

/**
 * 确保目录存在，如果不存在则递归创建
 */
const ensureDirectoryExists = (dirPath: string): void => {
  if (!fs.existsSync(dirPath)) {
    fs.mkdirSync(dirPath, { recursive: true });
  }
};

/**
 * 保存 Swagger 备份文件
 * @param swaggerJson - Swagger JSON 字符串
 * @param backupPath - 备份文件路径，支持 {timestamp} 占位符（可选）
 * @returns 实际保存的文件路径
 */
export const saveSwaggerBackup = (swaggerJson: string, backupPath: string): string => {
  try {
    let finalPath = backupPath;
    
    // 如果包含 {timestamp} 占位符，则替换
    if (backupPath.includes('{timestamp}')) {
      // 格式: yyyy-MM-dd hh-mm-ss (例如: 2024-11-02 15-30-45)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const timestamp = `${year}-${month}-${day} ${hours}-${minutes}-${seconds}`;
      finalPath = backupPath.replace(/\{timestamp\}/g, timestamp);
    }

    // 如果没有文件扩展名，自动添加 .json
    if (!finalPath.endsWith('.json')) {
      finalPath = finalPath + '.json';
    }

    // 确保目录存在
    const dirPath = path.dirname(finalPath);
    ensureDirectoryExists(dirPath);

    // 格式化 JSON 以便阅读
    const formattedJson = JSON.stringify(JSON.parse(swaggerJson), null, 2);

    // 写入文件
    fs.writeFileSync(finalPath, formattedJson, 'utf-8');

    return finalPath;
  } catch (error) {
    throw new Error(`Failed to save Swagger backup: ${(error as Error).message}`);
  }
};

